import { isNonNullish } from "remeda";
import type { TColumnHeaderProps } from "./components";
import { insert } from "../utils";
import type {
  CellRange,
  Column,
  GridApi,
  IMenuActionParams,
  ProcessDataFromClipboardParams,
  RowNode,
  SendToClipboardParams,
} from "ag-grid-community";
import { parseNumber } from "../numbers";
import { formatRunsTable } from "./runsTableFormat";
import type { PageProductsFragmentFragment } from "../../__generated__/gql/graphql";

type TRowDataForSeparator = {
  period?: string;
  rowType?: string;
  firstOfType?: boolean;
};

function clearCellRangeSelectionCompat(api: GridApi) {
  const gridApi = api as GridApi & { clearCellRangeSelection?: () => void };

  if (typeof gridApi.clearCellRangeSelection === "function") {
    gridApi.clearCellRangeSelection();
  } else {
    const legacyApi = api as unknown as { [key: string]: unknown };
    const clearRangeSelection = legacyApi.clearRangeSelection as (() => void) | undefined;
    if (typeof clearRangeSelection === "function") {
      clearRangeSelection();
    }
  }
}

function getSelectedRowIndexes(api: GridApi): number[] {
  const cellRanges = api.getCellRanges() as CellRange[] | null | undefined;
  if (!cellRanges?.length) return [];

  const rowIndexes = new Set<number>();

  for (const range of cellRanges) {
    const startRowIndex = range.startRow?.rowIndex;
    const endRowIndex = range.endRow?.rowIndex;
    if (startRowIndex === undefined || endRowIndex === undefined) continue;

    const from = Math.min(startRowIndex, endRowIndex);
    const to = Math.max(startRowIndex, endRowIndex);

    for (let rowIndex = from; rowIndex <= to; rowIndex++) {
      rowIndexes.add(rowIndex);
    }
  }

  return [...rowIndexes].sort((a, b) => a - b);
}

function getSeparatorDataRowPositions(api: GridApi): number[] {
  const rowIndexes = getSelectedRowIndexes(api);
  if (!rowIndexes.length) return [];

  const separatorPositions: number[] = [];

  rowIndexes.forEach((rowIndex, dataRowIndex) => {
    const node = api.getDisplayedRowAtIndex(rowIndex) as RowNode<TRowDataForSeparator> | null;
    const data = node?.data;

    const hasPeriod = !!data?.period;
    const isNotMonth = data?.rowType !== "mth";
    const isFirstOfType = !!data?.firstOfType;

    if (hasPeriod && isNotMonth && isFirstOfType) {
      separatorPositions.push(dataRowIndex);
    }
  });

  return separatorPositions;
}

function getRangeAsText(api: GridApi, options: { includeHeaders?: boolean; includeGroupHeaders?: boolean }): string {
  let captured = "";
  const previous = api.getGridOption("sendToClipboard");
  api.setGridOption("sendToClipboard", (params: SendToClipboardParams) => {
    captured = params.data;
  });
  try {
    api.copySelectedRangeToClipboard(options);
  } finally {
    api.setGridOption("sendToClipboard", previous);
  }
  return captured;
}

function writeClipboard(text: string) {
  return navigator.clipboard.writeText(text).catch(() => {
    // Nothing else we can do without the clipboard; the copy simply won't land.
  });
}

function insertSeparatorLines(
  text: string,
  { dataRowPositions, headerLineCount }: { dataRowPositions: number[]; headerLineCount: number },
): string {
  if (!dataRowPositions.length) return text;
  const lines = text.split("\n");
  const positions = dataRowPositions.map((dataIndex) => headerLineCount + dataIndex).sort((a, b) => b - a);
  return positions.reduce<string[]>((acc, lineIndex) => insert(acc, lineIndex, ""), lines).join("\n");
}

export function buildRunsTableClipboardText(
  tsv: string,
  dataRowPositions: number[],
  { monospace = false }: { monospace?: boolean } = {},
): string {
  const lines = tsv.replace(/\r/g, "").split("\n");
  const withSeparators = [...dataRowPositions]
    .sort((a, b) => b - a)
    .reduce<string[]>((acc, dataIndex) => insert(acc, 1 + dataIndex, ""), lines);
  const table = formatRunsTable(withSeparators.join("\n"));
  if (!table) return "";
  return monospace ? `\`\`\`\n${table}\n\`\`\`` : table;
}

export function insertLinesWithPeriodHeader(
  text: string,
  { insertPos, lines }: { insertPos: number; lines: string[] },
) {
  const newText = lines.reduceRight((acc, line) => insert(acc, insertPos, line), text.split("\n"));
  const periodHeader = newText[lines.length];
  const updatedText = [...newText.slice(0, lines.length), periodHeader, ...newText.slice(lines.length + 1)];
  return updatedText.join("\n");
}

export function copyWithHeaders(params: IMenuActionParams) {
  const api = params.api;
  if (api) {
    const separatorPositions = getSeparatorDataRowPositions(api);
    const text = getRangeAsText(api, { includeHeaders: true });
    writeClipboard(insertSeparatorLines(text, { dataRowPositions: separatorPositions, headerLineCount: 1 }));
  }
}

function copyRangeWithPeriodColumn(
  params: IMenuActionParams,
  format: (text: string, separatorPositions: number[]) => string,
) {
  const api = params.api;
  if (!api) return;

  // need to deep copy the ranges before modifying, otherwise getCellRanges returns a reference
  // and when we clear selection later it empties our saved ranges too
  const currentRanges = api.getCellRanges();
  const originalRanges = currentRanges?.map((range) => ({
    startRow: range.startRow,
    endRow: range.endRow,
    columns: [...range.columns],
    startColumn: range.startColumn,
  }));

  const rangeUpdateMap = addPeriodToSelection({ api });
  if (rangeUpdateMap === undefined) return;

  clearCellRangeSelectionCompat(api);
  api.addCellRange(rangeUpdateMap);
  const separatorPositions = getSeparatorDataRowPositions(api);
  const text = getRangeAsText(api, { includeHeaders: true });
  writeClipboard(format(text, separatorPositions));

  // put back the original selection so next copy doesnt include the period column
  clearCellRangeSelectionCompat(api);
  if (originalRanges && originalRanges.length > 0) {
    for (const range of originalRanges) {
      api.addCellRange({
        rowStartIndex: range.startRow?.rowIndex ?? null,
        rowEndIndex: range.endRow?.rowIndex ?? null,
        columns: range.columns,
      });
    }
  }
}

export function copyWithHeadersAndMonths(params: IMenuActionParams) {
  copyRangeWithPeriodColumn(params, (text, separatorPositions) =>
    insertSeparatorLines(text, { dataRowPositions: separatorPositions, headerLineCount: 1 }),
  );
}

export function copyForIm(params: IMenuActionParams) {
  copyRangeWithPeriodColumn(params, (text, separatorPositions) =>
    buildRunsTableClipboardText(text, separatorPositions),
  );
}

function getLastNonAdhocRowIndex(api: GridApi): number {
  for (let rowIndex = api.getDisplayedRowCount() - 1; rowIndex >= 0; rowIndex--) {
    const rowType = (api.getDisplayedRowAtIndex(rowIndex) as RowNode<TRowDataForSeparator> | null)?.data?.rowType;
    if (rowType && rowType !== "adhoc" && rowType !== "insert_adhoc") return rowIndex;
  }
  return -1;
}

export function copyColumnsWithHeadersAndMonths({ api, columns }: { api: GridApi; columns: Column[] }): boolean {
  if (!columns.length) return false;

  const lastRowIndex = getLastNonAdhocRowIndex(api);
  if (lastRowIndex < 0) return false;

  const displayed = api.getAllDisplayedColumns() ?? [];
  const orderedColumns = [...columns].sort((a, b) => displayed.indexOf(a) - displayed.indexOf(b));

  // Period column ("1") first so the months line up on the left, matching Copy with headers & months.
  const periodColumn = api.getColumn("1");
  const rangeColumns = (periodColumn ? [periodColumn, ...orderedColumns] : orderedColumns).filter(Boolean) as Column[];

  clearCellRangeSelectionCompat(api);
  api.addCellRange({ columns: rangeColumns, rowStartIndex: 0, rowEndIndex: lastRowIndex });

  const separatorPositions = getSeparatorDataRowPositions(api);
  const text = getRangeAsText(api, { includeHeaders: true });
  // Mobile copy is pasted into chat apps, so wrap in a monospace block for aligned columns.
  writeClipboard(buildRunsTableClipboardText(text, separatorPositions, { monospace: true }));

  clearCellRangeSelectionCompat(api);
  return true;
}

export function copyRangeToClipboard(api: GridApi) {
  const separatorPositions = getSeparatorDataRowPositions(api);
  const text = getRangeAsText(api, { includeHeaders: false, includeGroupHeaders: false });
  writeClipboard(insertSeparatorLines(text, { dataRowPositions: separatorPositions, headerLineCount: 0 }));
}

export function copyWithMetadata({
  params,
  products,
}: {
  params: IMenuActionParams;
  products: PageProductsFragmentFragment[];
}) {
  const api = params.api;

  if (!api) throw new Error("No grid api provided");

  const rangeUpdateMap = addPeriodToSelection({ api });
  if (rangeUpdateMap === undefined) {
    return;
  }

  clearCellRangeSelectionCompat(api);
  api.addCellRange(rangeUpdateMap);

  const separatorPositions = getSeparatorDataRowPositions(api);

  // Only filter out the MAIN period column (colId === "1")
  // Shadow period columns (field === "period" but colId !== "1") must be included for alignment
  const existingColumns = api
    .getCellRanges()
    ?.flatMap((cellRange) => cellRange.columns)
    .filter((column) => column?.getColId() !== "1");

  const headerComponents = existingColumns?.map(
    (column) => column?.getColDef()?.headerComponentParams,
  ) as TColumnHeaderProps[];

  const columnLabels = headerComponents?.map((headerComponent) => headerComponent?.columnLabels) || [];

  // IMPORTANT: Do NOT use .filter(Boolean) on these arrays - empty values must be preserved for alignment
  const artisType: string[] = headerComponents?.map((headerComponent) => headerComponent?.artisType || "") || [];

  const gridId: string[] = headerComponents?.map((headerComponent) => headerComponent?.gridId || "") || [];

  const uoms: string[] =
    headerComponents?.map(
      (headerComponent) => headerComponent?.columnLabels?.uomDisplay ?? headerComponent?.columnLabels?.uom ?? "",
    ) || [];

  const selectorFn = selector({ products });

  // Build fieldLabels with same length as other arrays (no filtering)
  const fieldLabels = gridId.map((id, index) => {
    if (id && artisType[index]) {
      const field = selectorFn({ gridId: id, artisType: artisType[index] });
      return field ? fieldNameToLabel(field) : "";
    }
    return "";
  });

  const attrToStr = (attr: string[]) => {
    return `\t${attr.join("\t")}`;
  };

  const text = getRangeAsText(api, { includeHeaders: true });

  // IMPORTANT: Do NOT use .filter(Boolean) - empty values must be preserved for column alignment
  const descriptions = columnLabels?.map((columnLabel) => columnLabel?.description || "") || [];
  const productIds = headerComponents?.map((headerComponent) => headerComponent?.productId || "") || [];
  const packages = columnLabels?.map((columnLabel) => columnLabel?.package || "") || [];

  const lines = [
    attrToStr(descriptions),
    attrToStr(productIds),
    attrToStr(packages),
    attrToStr(fieldLabels),
    attrToStr(uoms),
  ];

  const withMetadata = insertLinesWithPeriodHeader(text, { insertPos: 1, lines });
  writeClipboard(
    insertSeparatorLines(withMetadata, { dataRowPositions: separatorPositions, headerLineCount: 1 + lines.length }),
  );
}

function processParentheticalExplanations(col: (string | null | undefined)[][]) {
  return col.map((values) =>
    values.map((value) => {
      if (value?.match(/.*\(.*\)/)) {
        return `-${value.match(/[\d.]+/g)?.join("")}`;
      }
      return value?.match(/[-\d.]+/g)?.join("");
    }),
  );
}

export function defaultSelector(artisType: string) {
  return ["eod", "customer_curve", "global"].includes(artisType) ? "value" : "fv";
}

function selector({ products }: { products: PageProductsFragmentFragment[] }) {
  return ({ gridId, artisType }: { gridId: string; artisType: string }) => {
    return products?.find((product) => product.id === gridId)?.column_field_selector || defaultSelector(artisType);
  };
}

function pastedValueStringToFloat(col: (string | null | undefined)[][]) {
  return col.map((values) => values.map((value) => parseNumber(value)?.toString() || ""));
}

export function processDataFromClipboard(params: ProcessDataFromClipboardParams): string[][] {
  const data = params.data;
  const processedParentheticalExplanations = processParentheticalExplanations(data);
  return pastedValueStringToFloat(processedParentheticalExplanations);
}

export function addPeriodToSelection({ api }: { api: GridApi }) {
  const periodColumn = api.getColumn("1");
  const cellRanges = api.getCellRanges();
  const existingColumns = cellRanges?.flatMap((cellRange) => cellRange.columns) || [];

  const startRows = cellRanges?.map((cellRange) => cellRange?.startRow?.rowIndex).filter(isNonNullish) ?? [];
  const endRows = cellRanges?.map((cellRange) => cellRange?.endRow?.rowIndex).filter(isNonNullish) ?? [];

  if (startRows.length > 0 && endRows.length > 0) {
    const minimumStartRow = Math.min(...startRows);
    const maximumEndRow = Math.min(...endRows);

    const columns = (
      periodColumn && existingColumns?.includes(periodColumn) ? existingColumns : [periodColumn, ...existingColumns]
    )?.filter(Boolean);

    return {
      columns,
      rowEndIndex: minimumStartRow,
      rowStartIndex: maximumEndRow,
    };
  }
}

function fieldNameToLabel(fieldName: string) {
  return fieldName.replaceAll("-", " ").toUpperCase();
}
